import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { getCategorySubCategoryKey } from 'legacy/lut/components/widgets/public/partner360/commitment/planChangesLogDialog/components/planChangesLogDialogContent/helpers/getCategorySubCategoryKey'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import { getFormatDate } from 'core/classes/helpers/DateHelper'
import { ChangesPlanningSection, IPlanChangeLog } from 'partner360/models/planning/IPlanChangeLog'
import { fetchPlanChangesLog } from 'partner360/services/planning/fetchPlanChangesLog'
import { Skeleton } from 'shared/components/skeleton'

import { LogsPaging } from './components/logsPaging/LogsPaging'
import { fields } from './configs'
import { TRANSLATION_KEYS } from './translations'

import styles from './PlanChangesLogDialogContent.module.scss'

interface IPlanChangesLogDialogContentProps {
  partnerId: string
  programType: ProgramTypes
  fYear: number
  changeDate?: string
  isDistributor: boolean
  onClose: () => void
}

export const PlanChangesLogDialogContent = ({
  partnerId,
  changeDate,
  fYear,
  programType,
  isDistributor,
  onClose,
}: IPlanChangesLogDialogContentProps) => {
  const [data, setData] = useState<Record<string, IPlanChangeLog[]>>({})
  const [isLoading, setIsLoading] = useState(false)
  const infoRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [element, setElement] = useState<HTMLDivElement | null>(null)

  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0)

  const ownerId = useSelector(selectAppOwnerId)

  const items = useMemo(() => {
    const key = Object.keys(data)[selectedGroupIndex]
    return data[key] || []
  }, [selectedGroupIndex, data])

  const { subCategory, category, modified, modifiedBy } = useMemo(() => {
    if (!items[0]) {
      return { subCategory: '', category: '', modified: '', modifiedBy: '' }
    }

    return {
      subCategory: items[0].SubCategory,
      category: items[0].Category,
      modified: items[0].Modified,
      modifiedBy: items[0].ModifiedBy,
    }
  }, [items])

  const groupedData = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        acc[item.PlanningSection] = item
        return acc
      },
      {} as Record<string, IPlanChangeLog>
    )
  }, [items])

  const formatPeriod = useCallback((period: string, currentYear: number) => {
    const quarterMatch = period.match(/^PlanCYQ(\d)$/)
    if (quarterMatch) {
      return `Q${quarterMatch[1]}`
    }

    switch (period) {
      case 'PlanCY':
        return `${currentYear} total`
      case 'PlanNY':
        return `${currentYear + 1} total`
      case 'PlanN2Y':
        return `${currentYear + 2} total`
      default:
        return period
    }
  }, [])

  const fetch = useCallback(async () => {
    setIsLoading(true)
    const res = await fetchPlanChangesLog({
      programType,
      changeDate,
      fYear,
      partnerId,
      isDistributor,
      ownerId,
    })

    const data = res.reduce<Record<string, IPlanChangeLog[]>>((acc, log) => {
      const key = getCategorySubCategoryKey({
        category: log.Category,
        subCategory: log.SubCategory,
      })

      if (!acc[key]) {
        acc[key] = []
      }

      acc[key].push(log)

      return acc
    }, {})
    setData(data)

    setIsLoading(false)
  }, [changeDate, fYear, isDistributor, ownerId, partnerId, programType])

  const setWrapperRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setElement(node)
      setWidth(node.offsetWidth)
    }
  }, [])

  useEffect(() => {
    fetch()
  }, [fetch])

  useEffect(() => {
    if (!element) return

    const resizeObserver = new ResizeObserver(() => {
      setWidth(element.offsetWidth)
    })

    resizeObserver.observe(element)

    return () => resizeObserver.disconnect()
  }, [element])

  if (isLoading) {
    return <Skeleton width={'500px'} height={'400px'} />
  }

  if (!Object.keys(data).length) {
    return (
      <div className={styles.placeholder}>
        <Translation text={TRANSLATION_KEYS.placeholder} />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div ref={infoRef} className={styles.info} style={{ maxWidth: width || 'auto' }}>
        <Translation
          span
          text={TRANSLATION_KEYS.title}
          htmlAvailable
          replaceParams={[
            { key: '{user}', value: modifiedBy },
            {
              key: '{date}',
              value: getFormatDate(modified, 'custom', 'DD MMM YYYY') || '',
            },
          ]}
        />
      </div>

      <div className={styles.info}>
        <Translation
          span
          text={TRANSLATION_KEYS.category}
          htmlAvailable
          replaceParams={[
            { key: '{category}', value: category },
            {
              key: '{subcategory}',
              value: subCategory,
            },
          ]}
        />
      </div>
      <LogsPaging
        selectedGroupIndex={selectedGroupIndex}
        changeGroupIndex={setSelectedGroupIndex}
        groups={data}
        subCategory={subCategory}
      />
      <div ref={setWrapperRef} className={styles.wrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.emptyHeader}>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((period) => (
              <tr key={period}>
                <td className={styles.period}>{formatPeriod(period, fYear)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <RenderWhen condition={groupedData[ChangesPlanningSection.FinancialPlan] !== undefined}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th colSpan={2}>
                  <Translation text={TRANSLATION_KEYS.headFin} />
                </th>
              </tr>
            </thead>
            <tbody>
              {fields.map((period) => (
                <tr key={period}>
                  <td>{groupedData[ChangesPlanningSection.FinancialPlan]?.[period] || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </RenderWhen>

        <RenderWhen condition={groupedData[ChangesPlanningSection.SOBPlan] !== undefined}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>
                  <Translation text={TRANSLATION_KEYS.headSob} />
                </th>
              </tr>
            </thead>
            <tbody>
              {fields.map((period) => (
                <tr key={period}>
                  <td>{groupedData[ChangesPlanningSection.SOBPlan]?.[period] || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </RenderWhen>

        <RenderWhen condition={groupedData[ChangesPlanningSection.UnitsPlan] !== undefined}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>
                  <Translation text={TRANSLATION_KEYS.headUnits} />
                </th>
              </tr>
            </thead>
            <tbody>
              {fields.map((period) => (
                <tr key={period}>
                  <td>{groupedData[ChangesPlanningSection.UnitsPlan]?.[period] || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </RenderWhen>
      </div>

      <div className={styles.footer}>
        <Button onClick={onClose}>
          <Translation text={TRANSLATION_KEYS.ok} />
        </Button>
      </div>
    </div>
  )
}
